import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';
import SourceBadge from '../components/SourceBadge';

type Kind = {
  code: string;
  title: string;
  is_active: boolean;
  sort_order: number;
};

type DataSource = {
  code: string;
  title: string;
  kind: string;
  kind_title?: string;
  color: string | null;
  is_active: boolean;
};

const emptySource = { code: '', title: '', kind: '', color: '#6c757d' };
const emptyKind = { code: '', title: '' };

const DataSources: React.FC = () => {
  const [kinds, setKinds] = useState<Kind[]>([]);
  const [items, setItems] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [newSource, setNewSource] = useState({ ...emptySource });
  const [newKind, setNewKind] = useState({ ...emptyKind });
  const [saving, setSaving] = useState(false);

  const activeKinds = kinds.filter((k) => k.is_active);

  const load = async () => {
    setLoading(true);
    try {
      const [k, s] = await Promise.all([
        api.get('/data-source-kinds', { params: { all: 1 } }),
        api.get('/data-sources'),
      ]);
      const kindList: Kind[] = k.data.items || [];
      setKinds(kindList);
      setItems(s.data.items || []);
      setNewSource((prev) => ({
        ...prev,
        kind: prev.kind || kindList.find((x) => x.is_active)?.code || '',
      }));
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const createKind = async () => {
    if (!newKind.code.trim() || !newKind.title.trim()) {
      toast.error('Нужны code и название типа');
      return;
    }
    try {
      await api.post('/data-source-kinds', {
        code: newKind.code.trim(),
        title: newKind.title.trim(),
      });
      toast.success('Тип добавлен');
      setNewKind({ ...emptyKind });
      void load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const patchKind = async (code: string, patch: Partial<Kind>) => {
    try {
      await api.patch(`/data-source-kinds/${code}`, patch);
      void load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const createSource = async () => {
    if (!newSource.code.trim() || !newSource.title.trim()) {
      toast.error('Нужны code и title');
      return;
    }
    if (!newSource.kind) {
      toast.error('Выберите kind из справочника');
      return;
    }
    setSaving(true);
    try {
      await api.post('/data-sources', {
        code: newSource.code.trim(),
        title: newSource.title.trim(),
        kind: newSource.kind,
        color: newSource.color,
      });
      toast.success('Источник сохранён');
      setNewSource({ ...emptySource, kind: newSource.kind });
      void load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const changeSourceKind = async (s: DataSource, kind: string) => {
    if (!kind || kind === s.kind) return;
    try {
      await api.patch(`/data-sources/${s.code}`, { kind });
      void load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  const toggleActive = async (s: DataSource) => {
    try {
      await api.patch(`/data-sources/${s.code}`, { is_active: !s.is_active });
      void load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  return (
    <div className="container-fluid py-3">
      <h2 className="mb-3">Источники</h2>
      <p className="text-muted">
        Сначала ведётся справочник <strong>типов (kind)</strong>, затем у источника выбирается тип из
        списка.
      </p>

      <h4 className="mt-3">Типы (kind)</h4>
      <Alert variant="secondary" className="py-2">
        Расширяемый список. Новые значения добавляются формой ниже; выключенные нельзя выбрать для
        новых источников.
      </Alert>

      {loading ? (
        <Spinner size="sm" />
      ) : (
        <Table
          size="sm"
          hover
          responsive
          variant="dark"
          className="table-dark"
          style={{ maxWidth: 720 }}
        >
          <thead>
            <tr>
              <th>code</th>
              <th>Название</th>
              <th>active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => (
              <tr key={k.code}>
                <td>
                  <code>{k.code}</code>
                </td>
                <td>
                  <Form.Control
                    size="sm"
                    defaultValue={k.title}
                    key={`${k.code}-${k.title}`}
                    onBlur={(e) => {
                      const t = e.target.value.trim();
                      if (t && t !== k.title) void patchKind(k.code, { title: t });
                    }}
                  />
                </td>
                <td>{k.is_active ? 'да' : 'нет'}</td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => void patchKind(k.code, { is_active: !k.is_active })}
                  >
                    {k.is_active ? 'Выключить' : 'Включить'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Row className="g-2 align-items-end mb-4" style={{ maxWidth: 640 }}>
        <Col md={3}>
          <Form.Label>code типа</Form.Label>
          <Form.Control
            placeholder="parser"
            value={newKind.code}
            onChange={(e) => setNewKind((k) => ({ ...k, code: e.target.value }))}
          />
        </Col>
        <Col md={4}>
          <Form.Label>Название</Form.Label>
          <Form.Control
            placeholder="Парсер"
            value={newKind.title}
            onChange={(e) => setNewKind((k) => ({ ...k, title: e.target.value }))}
          />
        </Col>
        <Col md={3}>
          <Button onClick={() => void createKind()}>Добавить тип</Button>
        </Col>
      </Row>

      <hr />

      <h4>Источники</h4>
      {loading ? (
        <Spinner size="sm" />
      ) : (
        <Table size="sm" hover responsive variant="dark" className="table-dark">
          <thead>
            <tr>
              <th>Источник</th>
              <th>code</th>
              <th style={{ minWidth: 180 }}>kind</th>
              <th>active</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.code}>
                <td>
                  <SourceBadge code={s.code} title={s.title} color={s.color} />
                </td>
                <td>
                  <code>{s.code}</code>
                </td>
                <td>
                  <Form.Select
                    size="sm"
                    value={s.kind}
                    onChange={(e) => void changeSourceKind(s, e.target.value)}
                  >
                    {!activeKinds.some((k) => k.code === s.kind) && (
                      <option value={s.kind}>{s.kind_title || s.kind} (выкл.)</option>
                    )}
                    {activeKinds.map((k) => (
                      <option key={k.code} value={k.code}>
                        {k.title} ({k.code})
                      </option>
                    ))}
                  </Form.Select>
                </td>
                <td>{s.is_active ? 'да' : 'нет'}</td>
                <td>
                  <Button size="sm" variant="outline-secondary" onClick={() => void toggleActive(s)}>
                    {s.is_active ? 'Выключить' : 'Включить'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <h5 className="mt-3">Регистрация / обновление источника</h5>
      <Row className="g-2 align-items-end" style={{ maxWidth: 960 }}>
        <Col md={2}>
          <Form.Label>code</Form.Label>
          <Form.Control
            value={newSource.code}
            onChange={(e) => setNewSource((s) => ({ ...s, code: e.target.value }))}
          />
        </Col>
        <Col md={3}>
          <Form.Label>title</Form.Label>
          <Form.Control
            value={newSource.title}
            onChange={(e) => setNewSource((s) => ({ ...s, title: e.target.value }))}
          />
        </Col>
        <Col md={3}>
          <Form.Label>kind</Form.Label>
          <Form.Select
            value={newSource.kind}
            onChange={(e) => setNewSource((s) => ({ ...s, kind: e.target.value }))}
          >
            <option value="">— выберите —</option>
            {activeKinds.map((k) => (
              <option key={k.code} value={k.code}>
                {k.title} ({k.code})
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Label>color</Form.Label>
          <Form.Control
            type="color"
            value={newSource.color}
            onChange={(e) => setNewSource((s) => ({ ...s, color: e.target.value }))}
          />
        </Col>
        <Col md={2}>
          <Button onClick={() => void createSource()} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Сохранить'}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default DataSources;
